import { type ReactElement, Suspense, useCallback, useMemo } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';

import { useAuthContext } from '@/common/contexts/AuthContext';
import { useCurrentEmployeeContext } from '@/common/contexts/CurrentEmployeeContext';
import type { GetCurrentEmployeeQuery } from '@/common/graphql';
import { DateUtils } from '@/common/utils';

import { LazyLoadErrorBoundary } from '../../lazy-load-error';
import { Spinner } from '../../spinner';

type AccessFlagType = GetCurrentEmployeeQuery['currentEmployee']['accessFlag'];
type AccessFlagKey = keyof Omit<AccessFlagType, '__typename'>;

export const ProtectedRoute = ({
  redirectPath,
  allowedFlag,
  children,
}: {
  // Array form added 2026-09-15 (research.md #36, spec.md FR-019) for Team Name Governance's
  // widened SUPER_ADMIN-or-ADMIN_PRIVATE route gate - OR'd together, matching accessFlag's own
  // single-flag lookup semantics for every pre-existing single-string caller unchanged.
  allowedFlag?: AccessFlagKey | AccessFlagKey[];
  redirectPath: string;
  children?: ReactElement;
}) => {
  const { authUser } = useAuthContext();
  const { currentEmployee } = useCurrentEmployeeContext();
  const location = useLocation();

  const isTokenValid = useCallback(() => {
    try {
      const minuteDifference = DateUtils.getMinuteDifference(new Date(), new Date(authUser.exp * 1000));
      return minuteDifference >= 0;
    } catch (_e) {
      return false;
    }
  }, [authUser.exp]);

  // Bug fix, 2026-09-16 (direct user report): the array branch below must preserve the SAME
  // "still loading -> undefined, not false" semantics the single-flag branch already has via
  // optional chaining - `Array.prototype.some` always returns a strict boolean, so
  // `flags.some(f => currentEmployee?.accessFlag?.[f])` collapsed every per-flag `undefined`
  // (currentEmployee not yet loaded) into `false`, causing an immediate wrongful redirect on
  // every reload/direct URL visit, before currentEmployee had a chance to load - reproducible
  // for every role, since the array branch never even reached the "did any flag come back true"
  // check while loading. Fixed by evaluating each flag individually first, then only concluding
  // `false` once every flag has actually resolved (none `undefined`).
  const flagResults = useMemo(() => {
    if (allowedFlag === undefined) return [];
    const flags = Array.isArray(allowedFlag) ? allowedFlag : [allowedFlag];
    return flags.map(flag => currentEmployee?.accessFlag?.[flag]);
  }, [allowedFlag, currentEmployee?.accessFlag]);

  const allowed = useMemo(() => {
    if (allowedFlag === undefined) return undefined;
    if (flagResults.some(result => result === true)) return true;
    if (flagResults.some(result => result === undefined)) return undefined;
    return false;
  }, [allowedFlag, flagResults]);

  // useLocation() (not window.location) so this is a React-tracked reactive value - required for
  // useMemo below to recompute correctly on client-side navigation, not just full page loads.
  const currentRoute = useMemo(() => `${location.pathname}${location.search}`, [location.pathname, location.search]);

  if (!isTokenValid()) {
    localStorage.setItem('redirect', currentRoute);
    return <Navigate to={redirectPath} />;
  }

  if (typeof allowed !== 'undefined' && !allowed) {
    localStorage.removeItem('redirect');
    return <Navigate to={redirectPath} />;
  }

  return (
    <LazyLoadErrorBoundary>
      <Suspense
        fallback={
          <div className="flex w-full items-center justify-center py-11">
            <Spinner />
          </div>
        }
      >
        {children ?? <Outlet />}
      </Suspense>
    </LazyLoadErrorBoundary>
  );
};
